import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { LeadPhotoGallery } from "../components/LeadPhotoGallery";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Home, ArrowLeft, Search, Filter, Users, MapPin, DollarSign, 
  Calendar, Eye, UserPlus, UserMinus, Clock, Shield, Coins
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const BUDGET_TIERS = [
  { value: "all", label: "All Budget Tiers" },
  { value: "Less than $1,000", label: "Less than $1,000" },
  { value: "$1,000–$5,000", label: "$1,000 – $5,000" },
  { value: "$5,000–$10,000", label: "$5,000 – $10,000" },
  { value: "$10,000+", label: "$10,000+" }
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "available", label: "Available" },
  { value: "assigned", label: "Assigned" },
  { value: "closed", label: "Closed" }
];

export default function AdminLeads() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [budgetFilter, setBudgetFilter] = useState("all");
  
  // Lead detail dialog
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [leadDetail, setLeadDetail] = useState(null);
  
  // Add contractor dialog
  const [showAddContractorDialog, setShowAddContractorDialog] = useState(false);
  const [selectedContractorId, setSelectedContractorId] = useState("");

  useEffect(() => {
    fetchData();
  }, [statusFilter, budgetFilter]);

  const fetchData = async () => {
    try {
      let url = `${API}/admin/leads?`;
      const params = [];
      
      if (statusFilter && statusFilter !== "all") params.push(`status=${statusFilter}`);
      if (budgetFilter && budgetFilter !== "all") params.push(`budget_tier=${encodeURIComponent(budgetFilter)}`);
      
      url += params.join("&");
      
      const [leadsRes, contractorsRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/contractors`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setLeads(leadsRes.data);
      setContractors(contractorsRes.data);
    } catch (error) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const viewLeadDetail = async (leadId) => {
    try {
      const response = await axios.get(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadDetail(response.data);
      setSelectedLead(leadId);
      setShowLeadDialog(true);
    } catch (error) {
      toast.error("Failed to load lead details");
    }
  };

  const addContractorToLead = async () => {
    if (!selectedLead || !selectedContractorId) return;
    
    try {
      await axios.post(
        `${API}/admin/leads/${selectedLead}/add-contractor?contractor_id=${selectedContractorId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Contractor added to lead");
      setShowAddContractorDialog(false);
      setSelectedContractorId("");
      fetchData();
      viewLeadDetail(selectedLead);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add contractor");
    }
  };

  const removeContractorFromLead = async (contractorId) => {
    if (!selectedLead) return;
    
    try {
      await axios.post(
        `${API}/admin/leads/${selectedLead}/remove-contractor?contractor_id=${contractorId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Contractor removed from lead");
      fetchData();
      viewLeadDetail(selectedLead);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove contractor");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getBudgetTierColor = (tier) => {
    switch (tier) {
      case "Less than $1,000": return "bg-gray-100 text-gray-800";
      case "$1,000–$5,000": return "bg-blue-100 text-blue-800";
      case "$5,000–$10,000": return "bg-purple-100 text-purple-800";
      case "$10,000+": return "bg-amber-100 text-amber-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "available": return "bg-green-100 text-green-800";
      case "assigned": return "bg-blue-100 text-blue-800";
      case "closed": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-leads-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/admin")}>
              <ArrowLeft className="w-5 h-5 mr-2" /> Back to Admin
            </Button>
            <div className="h-6 w-px bg-gray-200" />
            <h1 className="font-manrope font-bold text-xl text-[#1F2933]">Lead Management</h1>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933] hidden sm:inline">Contractor Hub LLC</span>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-sm text-[#1F2933]/60">Total Leads</p>
            <p className="text-2xl font-bold text-[#1F2933]">{leads.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-sm text-[#1F2933]/60">Available</p>
            <p className="text-2xl font-bold text-green-600">
              {leads.filter(l => l.status === "available").length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-sm text-[#1F2933]/60">Assigned</p>
            <p className="text-2xl font-bold text-blue-600">
              {leads.filter(l => l.status === "assigned").length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <p className="text-sm text-[#1F2933]/60">High Value ($5k+)</p>
            <p className="text-2xl font-bold text-amber-600">
              {leads.filter(l => l.job_budget_tier?.includes("$5,000") || l.job_budget_tier?.includes("$10,000")).length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-[#1F2933]/40" />
              <span className="text-sm font-medium text-[#1F2933]">Filters:</span>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={budgetFilter} onValueChange={setBudgetFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Budget Tier" />
              </SelectTrigger>
              <SelectContent>
                {BUDGET_TIERS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-[#1F2933]/60">
              No leads found matching your filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service</TableHead>
                  <TableHead>Budget Tier</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Credit Cost</TableHead>
                  <TableHead>Slots</TableHead>
                  <TableHead>Assigned</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.lead_id} data-testid={`lead-row-${lead.lead_id}`}>
                    <TableCell className="font-medium">{lead.category_name}</TableCell>
                    <TableCell>
                      <Badge className={getBudgetTierColor(lead.job_budget_tier)}>
                        {lead.job_budget_tier}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-[#1F2933]/70">
                        <MapPin className="w-4 h-4" /> {lead.location_zip || "N/A"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        <Coins className="w-4 h-4 text-amber-500" /> {lead.credit_cost}
                      </span>
                    </TableCell>
                    <TableCell>
                      {lead.available_slots} / {lead.max_contractors}
                    </TableCell>
                    <TableCell>
                      {lead.assigned_contractors?.length || 0}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(lead.status)}>
                        {lead.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[#1F2933]/60 text-sm">
                      {formatDate(lead.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewLeadDetail(lead.lead_id)}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Lead Detail Dialog */}
      <Dialog open={showLeadDialog} onOpenChange={setShowLeadDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
            <DialogDescription>
              View and manage lead assignments
            </DialogDescription>
          </DialogHeader>
          
          {leadDetail && (
            <div className="space-y-6 pt-4">
              {/* Lead Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-xs text-[#1F2933]/50 uppercase mb-1">Service</p>
                  <p className="font-medium">{leadDetail.lead?.category_name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#1F2933]/50 uppercase mb-1">Budget</p>
                  <Badge className={getBudgetTierColor(leadDetail.lead?.job_budget_tier)}>
                    {leadDetail.lead?.job_budget_tier}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-[#1F2933]/50 uppercase mb-1">Location</p>
                  <p className="text-[#1F2933]/70">{leadDetail.lead?.homeowner_address}</p>
                </div>
                <div>
                  <p className="text-xs text-[#1F2933]/50 uppercase mb-1">Status</p>
                  <Badge className={getStatusColor(leadDetail.lead?.status)}>
                    {leadDetail.lead?.status}
                  </Badge>
                </div>
              </div>

              {/* Homeowner Info */}
              <div className="p-4 bg-blue-50 rounded-xl">
                <p className="text-xs text-blue-800 uppercase mb-2 font-medium">Homeowner</p>
                <div className="space-y-1 text-sm">
                  <p><strong>Name:</strong> {leadDetail.lead?.homeowner_name}</p>
                  <p><strong>Email:</strong> {leadDetail.lead?.homeowner_email}</p>
                  <p><strong>Phone:</strong> {leadDetail.lead?.homeowner_phone}</p>
                </div>
              </div>

              {/* Customer-entered comments */}
              {(leadDetail.lead?.answers?.unlisted_service_description || leadDetail.lead?.answers?.additional_details) && (
                <div className="space-y-3">
                  {leadDetail.lead?.answers?.unlisted_service_description && (
                    <div className="p-4 bg-[#0FA3B1]/10 rounded-xl border border-[#0FA3B1]/30" data-testid="admin-unlisted-desc">
                      <p className="text-xs text-[#0FA3B1] uppercase mb-1 font-medium">Custom Request</p>
                      <p className="text-sm text-[#1F2933] whitespace-pre-wrap">{leadDetail.lead.answers.unlisted_service_description}</p>
                    </div>
                  )}
                  {leadDetail.lead?.answers?.additional_details && (
                    <div className="p-4 bg-blue-50/70 rounded-xl border border-blue-200" data-testid="admin-additional-details">
                      <p className="text-xs text-blue-800 uppercase mb-1 font-medium">Additional Details</p>
                      <p className="text-sm text-blue-900 whitespace-pre-wrap">{leadDetail.lead.answers.additional_details}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Project Photos */}
              <LeadPhotoGallery leadId={leadDetail.lead?.lead_id} token={token} />

              {/* Assigned Contractors */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-[#1F2933]">
                    Assigned Contractors ({leadDetail.assigned_contractor_details?.length || 0} / {leadDetail.lead?.max_contractors})
                  </h3>
                  {(leadDetail.lead?.available_slots > 0) && (
                    <Button
                      size="sm"
                      onClick={() => setShowAddContractorDialog(true)}
                    >
                      <UserPlus className="w-4 h-4 mr-1" /> Add Contractor
                    </Button>
                  )}
                </div>
                
                {leadDetail.assigned_contractor_details?.length > 0 ? (
                  <div className="space-y-2">
                    {leadDetail.assigned_contractor_details.map((contractor) => (
                      <div key={contractor.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{contractor.full_name || contractor.business_name}</p>
                          <p className="text-sm text-[#1F2933]/60">{contractor.email}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => removeContractorFromLead(contractor.user_id)}
                        >
                          <UserMinus className="w-4 h-4 mr-1" /> Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#1F2933]/60 text-sm py-4 text-center bg-gray-50 rounded-lg">
                    No contractors assigned yet.
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Contractor Dialog */}
      <Dialog open={showAddContractorDialog} onOpenChange={setShowAddContractorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Contractor to Lead</DialogTitle>
            <DialogDescription>
              Select a contractor to assign to this lead
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <Select value={selectedContractorId} onValueChange={setSelectedContractorId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a contractor" />
              </SelectTrigger>
              <SelectContent>
                {contractors
                  .filter(c => c.onboarding_complete)
                  .filter(c => !leadDetail?.lead?.assigned_contractors?.includes(c.user_id))
                  .map(c => (
                    <SelectItem key={c.user_id} value={c.user_id}>
                      {c.full_name || c.business_name} 
                      {c.is_insured && " (Insured)"} 
                      - Score: {c.contractor_score}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
            
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowAddContractorDialog(false)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-[#0FA3B1]" onClick={addContractorToLead} disabled={!selectedContractorId}>
                Add Contractor
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
